<script lang="ts" setup>
import { computed, ref, type DeepReadonly } from 'vue';
import { EntityTagList, ReportProblemLink } from '~/components';
import { AppTypography } from '~/components/base';
import { SourcesDialog } from '~/components/candidates';
import type { Campaign, EntityTag } from '~/types/Candidates';
import { campaignTypeLabel } from '~/utils/campaignTypeLabel';
import { $sanitizeHtml } from '~/utils/helpers';

// The single source of truth for how a campaign's details look. Both the
// /campaigns/[id] page and the desktop modal on the listing render this, so the
// two can't drift in type, spacing or colour.
//
// DeepReadonly because useCampaign hands back a `readonly()` ref; a mutable
// Campaign is assignable to it, so both callers work.
const {
  campaign,
  titleElement = 'h1',
  reportReturnTo,
} = defineProps<{
  campaign: DeepReadonly<Campaign>;
  titleElement?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';
  /**
   * Passed straight to ReportProblemLink. The modal caller sets it so Back
   * from the report returns to the listing with this campaign reopened; the
   * standalone page leaves it unset, since its own URL is the right target.
   */
  reportReturnTo?: string;
}>();

const sources = computed(() => campaign.sources ?? []);
const tags = computed<readonly EntityTag[]>(() => campaign.tags ?? []);

// Merges to a single "Party and Candidate Led" pill when both sides are
// attached — see campaignTypeLabel.
const typeLabel = computed(() => campaignTypeLabel(campaign));

// Description is stored as HTML on the backend; sanitize before rendering
// with v-html (same DOMPurify helper used by the terms/privacy pages).
const sanitizedDescription = computed(() =>
  $sanitizeHtml(campaign.description),
);

const sourcesVisible = ref(false);
</script>

<template>
  <div class="campaign-details flex flex-col gap-6">
    <AppTypography type="title-5" :element="titleElement">
      {{ campaign.title }}
    </AppTypography>

    <div class="campaign-details__divider" />

    <span class="campaign-details__tag inline-flex w-fit items-center">
      <AppTypography type="caption-bold" class="uppercase">{{
        typeLabel
      }}</AppTypography>
    </span>

    <!-- eslint-disable vue/no-v-html -->
    <div
      class="campaign-details__description font-body text-color-secondary"
      v-html="sanitizedDescription"
    ></div>
    <!-- eslint-enable vue/no-v-html -->

    <EntityTagList :tags="tags" />

    <button
      v-if="sources.length"
      type="button"
      data-testid="campaign-sources-toggle"
      class="campaign-details__sources flex items-center justify-between"
      @click="sourcesVisible = true"
    >
      <span class="flex items-center gap-2">
        <Icon name="solar:link-round-linear" class="shrink-0" />
        <AppTypography type="caption-bold" element="span"
          >Sources</AppTypography
        >
      </span>
      <Icon name="solar:alt-arrow-right-linear" class="shrink-0" />
    </button>

    <ReportProblemLink
      report-type="campaigns"
      :report-id="campaign.id"
      :return-to="reportReturnTo"
    />

    <SourcesDialog v-model:visible="sourcesVisible" :sources="[...sources]" />
  </div>
</template>

<style scoped lang="scss">
.campaign-details {
  &__divider {
    height: 1px;
    // Figma: borders/default (#e8e3db) — matches the candidate/party
    // profiles' About-tab divider; this app's `$border-color` is a
    // different (navy) palette.
    background-color: #e8e3db;

    @media (max-width: 639.98px) {
      background-color: $complementary-color-shade-1;
    }
  }

  &__tag {
    // Figma: this pill reads noticeably larger than the tight 2px/8px this
    // used to carry — matches the "Campaigns Screen - Popup" audit (VTW-548).
    padding: $spacing-xs $spacing-md;
    border-radius: $radius-2xl;
    background-color: #e6d6ac;
    color: $text-color-primary;
  }

  &__description {
    // The sanitized description may contain block tags (<p>...). Keep
    // paragraph spacing readable; the browser's default <p> margins would
    // otherwise be reset away by the global reset.
    :deep(p) {
      margin: 0;
    }

    :deep(p + p) {
      margin-top: $spacing-md;
    }
  }

  &__sources {
    width: 100%;
    padding: $spacing-md $spacing-lg;
    // Figma: borders/secondary (#827b71) — matches the candidate/party
    // profiles' Sources button; this app's `$border-color` is a different,
    // unrelated pale-grey value.
    border: 1px solid $border-color-tag;
    border-radius: $radius-sm;
    background: transparent;
    cursor: pointer;
  }
}
</style>
